import React, { useEffect, useState, useCallback, useMemo } from "react";
import { DataGrid, GridColDef, GridToolbar } from "@mui/x-data-grid";
import axios from "axios";
import HOST_URL from "../../../utils/Url";
import Card from "@mui/material/Card";
import { Box, Button, Divider, Grid, Stack, TextField, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import Switch from "@mui/material/Switch";
import { useNavigate, useLocation } from "react-router-dom";
import Chip from "@mui/material/Chip";
import { useTranslation } from "react-i18next";
import Paper from "@mui/material/Paper";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { toast } from "react-toastify";
import ToastApp from "../../../ToastApp";
import DeleteIcon from "@mui/icons-material/Delete";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import CircularProgress from "@mui/material/CircularProgress";
import { useFormik } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";
import { usePermissionData } from "../../../hooks/usePermissionData";
import { getSecureDevId } from "../../../utils/storageHelpers";


interface MenuPermission {
  isAdd: boolean;
  isEdit: boolean;
  isPrint: boolean;
  isDel: boolean;
}

interface DocData {
  docID: number;
  doc_Name: string;
  doc_shortname: string;
  session_year: string;
  entry_Date: string;
  inst_ID: number;
  parentDocId: number;
  parentLevel: number;
  userId: string;
  parentDoc_Name: string;
  srn: number;
  id: number;
  serialNo: number;
}

export default function DocType() {
  const [docs, setDocs] = useState<DocData[]>([]);
  const [columns, setColumns] = useState<GridColDef[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEdit, setIsEdit] = useState(false);
  const [deleteId, setDeleteId] = useState<string>("");

  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { permissionData, loading: permissionLoading } = usePermissionData();

  // Memoized fetch function
 const fetchDocData = useCallback(async () => {
  try {
    setIsLoading(true);

    // Get current user's department
    const devId = getSecureDevId();

    // Get current user's role
    let userRoleLocal = "";

    try {
      const userData = JSON.parse(
        localStorage.getItem("userdetail") || "[]"
      );

      if (userData?.length > 0) {
        const user = userData[0];

        userRoleLocal = user.roleName || user.role || "";
      }
    } catch (error) {
      console.error("Error getting user role:", error);
    }

    const isAdmin =
      userRoleLocal?.toLowerCase() === "admin" ||
      userRoleLocal?.toLowerCase() === "administrator";

    // Admin => all document types
    // Department user => only own department document types
    const filterDeptId = isAdmin
      ? -1
      : devId == null || devId === null
        ? -1
        : Number(devId);

    const collectData = {
      docID: -1,
      parentDocId: filterDeptId,
      userId: "",
    };

    console.log("========== DOCUMENT TYPE ==========");
    console.log("User Role:", userRoleLocal);
    console.log("Is Admin:", isAdmin);
    console.log("Secure Dev ID:", devId);
    console.log("Final parentDocId:", filterDeptId);
    console.log("API Payload:", collectData);

    const response = await axios.post(
      HOST_URL.HOST_URL2 + `OtherDocType/GetOtherDocType`,
      collectData
    );

    if (response.data?.isSuccess && Array.isArray(response.data.data)) {
      const docsWithIds = response.data.data.map(
        (doc: any, index: number) => ({
          ...doc,
          serialNo: index + 1,
          id: doc.docID,
        })
      );

      setDocs(docsWithIds);
    } else {
      setDocs([]);
      toast.error(
        response.data?.mesg || "Failed to load document types"
      );
    }
  } catch (error) {
    console.error("Error fetching data:", error);
    setDocs([]);
    toast.error("Failed to load document types");
  } finally {
    setIsLoading(false);
  }
}, []);


  // Initial data fetch
  useEffect(() => {
    fetchDocData();
  }, [fetchDocData]);

  // Setup columns based on permission data
  useEffect(() => {
    if (permissionLoading || docs.length === 0) return;

    const baseColumns: GridColDef[] = [
      {
        field: "serialNo",
        headerName: t("text.SrNo"),
        flex: 1,
        headerClassName: "MuiDataGrid-colCell",
        minWidth: 60
      },
      {
        field: "doc_Name",
        headerName: t("text.DocType"),
        flex: 1,
        headerClassName: "MuiDataGrid-colCell",
        minWidth: 150
      },
      {
        field: "doc_shortname",
        headerName: t("text.ShortName"),
        flex: 1,
        headerClassName: "MuiDataGrid-colCell",
        minWidth: 120
      },
    ];

    // Add actions column only if user has edit or delete permissions
    if (permissionData?.isEdit || permissionData?.isDel) {
      const actionsColumn: GridColDef = {
        field: "actions",
        headerClassName: "MuiDataGrid-colCell",
        headerName: t("text.Action"),
        width: 150,
        renderCell: (params) => (
          <Stack
            spacing={1}
            direction="row"
            sx={{ alignItems: "center", marginTop: "5px" }}
          >
            {permissionData?.isEdit && (
              <EditIcon
                style={{
                  fontSize: "20px",
                  color: "blue",
                  cursor: "pointer",
                }}
                className="cursor-pointer"
                onClick={() => handleEditClick(params.row)}
              />
            )}
            {permissionData?.isDel && (
              <DeleteIcon
                style={{
                  fontSize: "20px",
                  color: "red",
                  cursor: "pointer",
                }}
                onClick={() => {
                  setDeleteId(params.row.id.toString());
                  confirmDialog({
                    message: "Do you want to delete this record?",
                    header: "Delete Confirmation",
                    icon: "pi pi-info-circle",
                    acceptClassName: "p-button-danger",
                    accept: () => handleDeleteAccept(params.row.id),
                    reject: handleDeleteReject,
                  });
                }}
              />
            )}
          </Stack>
        ),
      };

      setColumns([actionsColumn, ...baseColumns]);
    } else {
      setColumns(baseColumns);
    }
  }, [permissionData, permissionLoading, docs.length, t]);

  // Navigation handlers
  const routeChangeAdd = () => {
    navigate(`/DocManagement/DocTypeAdd`);
  };

  const handleEditClick = (row: DocData) => {
    setIsEdit(true);
    formik.setValues({
      doc_Name: row.doc_Name || "",
      docID: row.docID || -1,
      doc_shortname: row.doc_shortname || "",
      session_year: row.session_year || "",
      entry_Date: row.entry_Date || dayjs().format("YYYY-MM-DD"),
      inst_ID: row.inst_ID || 0,
      parentDocId: row.parentDocId || 0,
      parentLevel: row.parentLevel || 0,
      userId: row.userId || "",
      parentDoc_Name: row.parentDoc_Name || "",
      srn: row.srn || 0,
      id: row.id || 0,
      serialNo: row.serialNo || 0,
    });
  };

  // Delete handlers
  const handleDeleteAccept = useCallback(async (id: number) => {
    try {
      const collectData = { docID: id };

      const response = await axios.post(
        HOST_URL.HOST_URL2 + `OtherDocType/DeleteOtherDocType`,
        collectData
      );

      if (response.data.isSuccess) {
        toast.success(response.data.mesg);
        fetchDocData();
      } else {
        toast.error(response.data.mesg);
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete document type");
    } finally {
      setDeleteId("");
    }
  }, [fetchDocData]);

  const handleDeleteReject = useCallback(() => {
    toast.warn("Deletion cancelled", { autoClose: 3000 });
    setDeleteId("");
  }, []);

  // Formik configuration
  const validationSchema = Yup.object({
    doc_Name: Yup.string()
      .required("Document type is required")
      .test(
        'not-blank',
        'Document type cannot be empty',
        (value: any) => value && value.trim() !== ''
      ),
  });

  const formik = useFormik({
    initialValues: {
      docID: -1,
      doc_Name: "",
      doc_shortname: "",
      session_year: "",
      entry_Date: dayjs().format("YYYY-MM-DD"),
      inst_ID: 0,
      parentDocId: 0,
      parentLevel: 0,
      userId: "",
      parentDoc_Name: "",
      srn: 0,
      id: 0,
      serialNo: 0,
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        const response = await axios.post(
          HOST_URL.HOST_URL2 + `OtherDocType/AddUpdateOtherDocType`,
          values
        );

        if (response.data.isSuccess) {
          toast.success(response.data.mesg);
          setIsEdit(false);
          resetForm();
          fetchDocData();
        } else {
          toast.error(response.data.mesg);
        }
      } catch (error) {
        console.error("Form submission error:", error);
        toast.error("Failed to save document type");
      }
    },
  });

  // Memoized adjusted columns
  const adjustedColumns = useMemo(() =>
    columns.map(column => ({ ...column })),
    [columns]
  );

  // Loading state handling
  if (permissionLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Card
        style={{
          width: "100%",
          backgroundColor: "#E9FDEE",
          border: ".5px solid #FF7722",
          marginTop: "3vh",
        }}
      >
        <Paper
          sx={{
            width: "100%",
            overflow: "hidden",
            "& .MuiDataGrid-colCell": {
              backgroundColor: `var(--grid-headerBackground)`,
              color: `var(--grid-headerColor)`,
              fontSize: 17,
              fontWeight: 900,
            },
          }}
          style={{ padding: "10px" }}
        >
          <ConfirmDialog />

          <Typography
            gutterBottom
            variant="h5"
            component="div"
            sx={{ padding: "20px" }}
            align="left"
          >
            {t("text.DocType")}
          </Typography>
          <Divider />

          <Box height={10} />

          {/* Form Section - Only show if user has permissions */}
          {(permissionData?.isEdit || permissionData?.isAdd) && (
            <form onSubmit={formik.handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={
                      <span>
                        {t("text.EnterDocName")}
                        <span style={{ color: "red" }}>*</span>
                      </span>
                    }
                    value={formik.values.doc_Name}
                    placeholder={t("text.EnterDocName")}
                    size="small"
                    fullWidth
                    name="doc_Name"
                    id="doc_Name"
                    style={{ backgroundColor: "white" }}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.doc_Name && Boolean(formik.errors.doc_Name)}
                    helperText={formik.touched.doc_Name && formik.errors.doc_Name}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label={t("text.EnterShortName")}
                    value={formik.values.doc_shortname}
                    placeholder={t("text.EnterShortName")}
                    size="small"
                    fullWidth
                    name="doc_shortname"
                    id="doc_shortname"
                    style={{ backgroundColor: "white" }}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Grid container spacing={2} justifyContent="center">
                    <Grid item xs={6} md={3}>
                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        style={{
                          backgroundColor: `var(--header-background)`,
                          color: "white",
                          marginTop: "10px",
                        }}
                      >
                        {isEdit ? t("text.update") : t("text.save")}
                      </Button>
                    </Grid>

                    <Grid item xs={6} md={3}>
                      <Button
                        type="button"
                        fullWidth
                        variant="contained"
                        style={{
                          backgroundColor: "#F43F5E",
                          color: "white",
                          marginTop: "10px",
                        }}
                        onClick={() => {
                          formik.resetForm();
                          setIsEdit(false);
                        }}
                      >
                        {t("text.reset")}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </form>
          )}

          {/* Data Grid Section */}
          <Box mt={3}>
            {isLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ width: "100%", backgroundColor: `var(--grid-background)` }}>
                <DataGrid
                  rows={docs}
                  columns={adjustedColumns}
                  autoHeight
                  slots={{
                    toolbar: GridToolbar,
                  }}
                  rowSpacingType="border"
                  pagination={true}
                  pageSizeOptions={[5, 10, 25, 50, 100]}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 5 } },
                  }}
                  slotProps={{
                    toolbar: {
                      showQuickFilter: true,
                    },
                  }}
                  disableRowSelectionOnClick
                />
              </Box>
            )}
          </Box>
        </Paper>
      </Card>
      <ToastApp />
    </>
  );
}


















// import React, { useEffect, useState } from "react";
// import { DataGrid, GridColDef, GridToolbar } from "@mui/x-data-grid";
// import axios from "axios";
// import HOST_URL from "../../../utils/Url";
// import Card from "@mui/material/Card";
// import { Box, Button, Divider, Grid, Stack, TextField, Typography } from "@mui/material";
// import EditIcon from "@mui/icons-material/Edit";
// import Switch from "@mui/material/Switch";
// import { useNavigate, useLocation } from "react-router-dom";
// import Chip from "@mui/material/Chip";
// import { useTranslation } from "react-i18next";
// import Paper from "@mui/material/Paper";
// import AddCircleIcon from "@mui/icons-material/AddCircle";
// import { toast } from "react-toastify";
// import ToastApp from "../../../ToastApp";
// import DeleteIcon from "@mui/icons-material/Delete";
// import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
// import CircularProgress from "@mui/material/CircularProgress";
// import { useFormik } from "formik";
// import * as Yup from "yup";
// import dayjs from "dayjs";

// interface MenuPermission {
//   isAdd: boolean;
//   isEdit: boolean;
//   isPrint: boolean;
//   isDel: boolean;
// }

// export default function DocType() {
//   const [Docs, setDocs] = useState([]);
//   const [columns, setColumns] = useState<any>([]);
//   const [isLoading, setIsLoading] = useState(true);
//   const location = useLocation();
//   const [permissionData, setPermissionData] = useState<MenuPermission>({
//     isAdd: false,
//     isEdit: false,
//     isPrint: false,
//     isDel: false,
//   });

//   let navigate = useNavigate();
//   const { t } = useTranslation();
//   const [toaster, setToaster] = useState(false);
//   const requiredFields = ['DocType'];
//   const [isEdit, setIsEdit] = useState(false);


//   useEffect(() => {
//     const dataString = localStorage.getItem("userdata");
//     // if (dataString) {
//     //     const data = JSON.parse(dataString);
//     //     if (data && data.length > 0) {
//     //         const userPermissionData = data[0]?.userPermission;
//     //         if (userPermissionData && userPermissionData.length > 0) {
//     //             const menudata = userPermissionData[0]?.parentMenu;
//     //             for (let index = 0; index < menudata.length; index++) {
//     //                 const childMenudata = menudata[index]?.childMenu;
//     //                 const pathrow = childMenudata.find(
//     //                     (x: any) => x.path === location.pathname
//     //                 );
//     //                 console.log("data", pathrow);
//     //                 if (pathrow) {
//     //                     setPermissionData(pathrow);
//     //                 }
//     //             }
//     //         }
//     //     }
//     // }
//     fetchDocData();
//   }, []);
//   // }, [isLoading]);
//   const routeChangeAdd = () => {
//     let path = `/DocManagement/DocTypeAdd`;
//     navigate(path);
//   };

//   // const handleSwitchChange = (
//   //     event: React.ChangeEvent<HTMLInputElement>,
//   //     value: any
//   // ) => {
//   //     console.log(value)
//   //     const collectData = {
//   //         fileClassid: value.id,
//   //         classDescription: value.classDescription,
//   //         shortName: value.shortName,
//   //         weedingOutAuthority: value.weedingOutAuthority,
//   //         isActive: event.target.checked,
//   //         userID: -1,
//   //         sortOrder: 0,
//   //     };
//   //     axios
//   //         .post(HOST_URL.HOST_URL2 + `FileClass/AddUpdateFileClass`, collectData)
//   //         .then((response) => {
//   //             if (response.data.isSuccess) {
//   //                 toast.success(response.data.mesg);
//   //                 fetchZonesData();
//   //             } else {
//   //                 toast.error(response.data.mesg);
//   //             }
//   //         });
//   // };

//   const routeChangeEdit = (row: any) => {
//     // let path = `/DocManagement/DocTypeEdit`;
//     // navigate(path, {
//     //   state: row,
//     // });
//     setIsEdit(true);
//     formik.setFieldValue("doc_Name", row.doc_Name)
//     formik.setFieldValue("docID", row.docID);
//     formik.setFieldValue("doc_shortname", row.doc_shortname);
//     formik.setFieldValue("session_year", row.session_year);
//     formik.setFieldValue("entry_Date", row.entry_Date);
//     formik.setFieldValue("inst_ID", row.inst_ID);
//     formik.setFieldValue("parentDocId", row.parentDocId);
//     formik.setFieldValue("parentLevel", row.parentLevel);
//     formik.setFieldValue("userId", row.userId);
//     formik.setFieldValue("parentDoc_Name", row.parentDoc_Name);
//     formik.setFieldValue("srn", row.srn);
//     formik.setFieldValue("serialNo", row.serialNo);
//     formik.setFieldValue("id", row.id);
//   };

//   let delete_id = "";

//   const accept = () => {
//     const collectData = {
//       docID: delete_id,
//     };
//     console.log("collectData " + JSON.stringify(collectData));
//     axios
//       .post(HOST_URL.HOST_URL2 + `OtherDocType/DeleteOtherDocType`,
//         collectData)
//       .then((response) => {
//         if (response.data.isSuccess) {
//           toast.success(response.data.mesg);
//         } else {
//           toast.error(response.data.mesg);
//         }
//         fetchDocData();
//       });
//   };

//   const reject = () => {
//     toast.warn("Rejected: You have rejected", { autoClose: 3000 });
//   };

//   const handledeleteClick = (del_id: any) => {
//     // console.log(del_id + " del_id ");
//     delete_id = del_id;
//     confirmDialog({
//       message: "Do you want to delete this record ?",
//       header: "Delete Confirmation",
//       icon: "pi pi-info-circle",
//       acceptClassName: "p=-button-danger",
//       accept,
//       reject,
//     });
//   };

//   const fetchDocData = async () => {
//     try {
//       const collectData = {
//         docID: -1,
//         parentDocId: 0,
//         userId: "",
//       };
//       console.log("collectData", collectData);
//       const response = await axios.post(
//         HOST_URL.HOST_URL2 + `OtherDocType/GetOtherDocType`,
//         collectData
//       );
//       console.log("result", response.data.data);
//       const data = response.data.data;
//       const DocsWithIds = data.map((doc: any, index: any) => ({
//         ...doc,
//         serialNo: index + 1,
//         id: doc.docID,
//       }));

//       setDocs(DocsWithIds);
//       setIsLoading(false);

//       if (data.length > 0) {
//         const columns: GridColDef[] = [
//           {
//             field: "actions",
//             headerClassName: "MuiDataGrid-colCell",
//             headerName: t("text.Action"),
//             width: 150,

//             renderCell: (params) => {
//               return [
//                 <Stack
//                   spacing={1}
//                   direction="row"
//                   sx={{ alignItems: "center", marginTop: "5px" }}
//                 >
//                   {/* {permissionData?.isEdit ? ( */}
//                   <EditIcon
//                     style={{
//                       fontSize: "20px",
//                       color: "blue",
//                       cursor: "pointer",
//                     }}
//                     className="cursor-pointer"
//                     onClick={() => routeChangeEdit(params.row)}
//                   />
//                   {/* ) : ( */}
//                   {/*  "" */}
//                   {/*)} */}
//                   {/*{permissionData?.isDel ? ( */}
//                   <DeleteIcon
//                     style={{
//                       fontSize: "20px",
//                       color: "red",
//                       cursor: "pointer",
//                     }}
//                     onClick={() => {
//                       handledeleteClick(params.row.id);
//                     }}
//                   />
//                   {/* ) : ( */}
//                   {/*  "" */}
//                   {/* )} */}
//                   {/* <Switch
//                                         checked={Boolean(params.row.isActive)}
//                                         style={{
//                                             color: params.row.isActive ? "green" : "#FE0000",
//                                         }}
//                                         onChange={(value: any) =>
//                                             handleSwitchChange(value, params.row)
//                                         }
//                                         inputProps={{
//                                             "aria-label": "Toggle Switch",
//                                         }}
//                                     /> */}
//                 </Stack>,
//               ];
//             },
//           },

//           // {
//           //   field: "empid",
//           //   headerName: "Emp Id",
//           //   flex: 1,
//           //   headerClassName: "MuiDataGrid-colCell",
//           // },

//           {
//             field: "serialNo",
//             headerName: t("text.SrNo"),
//             flex: 1,
//             headerClassName: "MuiDataGrid-colCell",
//             minWidth: 60
//           },

//           // {
//           //   field: "parentDoc_Name",
//           //   headerName: t("text.DocType"),
//           //   flex: 1,
//           //   headerClassName: "MuiDataGrid-colCell",
//           // },
//           {
//             field: "doc_Name",
//             headerName: t("text.DocType"),
//             flex: 1,
//             headerClassName: "MuiDataGrid-colCell",
//             minWidth: 150
//           },

//           {
//             field: "doc_shortname",
//             headerName: t("text.ShortName"),
//             flex: 1,
//             headerClassName: "MuiDataGrid-colCell",
//             minWidth: 120
//           },
//           // {
//           //     field: "empStatus",
//           //     headerName: "Emp status",
//           //     flex: 1,
//           //     headerClassName: "MuiDataGrid-colCell",
//           //     renderCell: (params) => [
//           //         <Stack direction="row" spacing={1}>
//           //             {params.row.isActive ? (
//           //                 <Chip
//           //                     label={t("Active")}
//           //                     color="success"
//           //                     style={{ fontSize: "14px" }}
//           //                 />
//           //             ) : (
//           //                 <Chip
//           //                     label={("InActive")}
//           //                     color="error"
//           //                     style={{ fontSize: "14px" }}
//           //                 />
//           //             )}
//           //         </Stack>,
//           //     ],
//           // },
//         ];
//         setColumns(columns as any);
//       }
//     } catch (error) {
//       console.error("Error fetching data:", error);
//       // setLoading(false);
//     }
//   };

//   const adjustedColumns = columns.map((column: any) => ({
//     ...column,
//   }));


//   const validationSchema = Yup.object({
//     doc_Name: Yup.string().test(
//       'required',

//       function (value: any) {
//         return value && value.trim() !== '';
//       }
//     ),
//   });


//   const formik = useFormik({
//     initialValues: {
//       docID: -1,
//       doc_Name: "",
//       doc_shortname: "",
//       session_year: "",
//       entry_Date: dayjs(new Date()).format("YYYY-MM-DD"),
//       inst_ID: 0,
//       parentDocId: 0,
//       parentLevel: 0,
//       userId: "",
//       parentDoc_Name: "",
//       srn: 0
//     },
//     validationSchema: validationSchema,
//     onSubmit: async (values) => {

//       const response = await axios.post(
//         HOST_URL.HOST_URL2 + `OtherDocType/AddUpdateOtherDocType`,
//         values
//       );
//       try {
//         setToaster(false);
//         toast.success(response.data.mesg);
//         setIsEdit(false);
//         formik.resetForm();
//         fetchDocData();
//         // navigate('/DocManagement/DocType');
//       } catch (error) {
//         setToaster(true);
//         toast.error(response.data.mesg);
//       }
//     },
//   });



//   return (
//     <>
//       <Card
//         style={{
//           width: "100%",
//           // height: "100%",
//           backgroundColor: "#E9FDEE",
//           border: ".5px solid #FF7722 ",
//           marginTop: "3vh",
//         }}
//       >
//         <Paper
//           sx={{
//             width: "100%",
//             overflow: "hidden",
//             "& .MuiDataGrid-colCell": {
//               backgroundColor: `var(--grid-headerBackground)`,
//               color: `var(--grid-headerColor)`,
//               fontSize: 17,
//               fontWeight: 900,
//             },
//           }}
//           style={{ padding: "10px" }}
//         >
//           <ConfirmDialog />

//           <Typography
//             gutterBottom
//             variant="h5"
//             component="div"
//             sx={{ padding: "20px" }}
//             align="left"
//           >
//             {t("text.DocType")}
//           </Typography>
//           <Divider />

//           <Box height={10} />

//           {/* <Stack direction="row" spacing={2} classes="my-2 mb-2">
//             permissionData?.isAdd == true && (
//             <Button
//               onClick={routeChangeAdd}
//               variant="contained"
//               endIcon={<AddCircleIcon />}
//               size="large"
//               style={{ backgroundColor: `var(--header-background)` }}
//             >
//               {t("text.add")}
//             </Button>
//             )}

//             {permissionData?.isPrint == true ? (
//               <Button variant="contained" endIcon={<PrintIcon />} size="large">
//                 {t("text.print")}
//               </Button>
//             ) : (
//               ""
//             )}
//           </Stack> */}

//           <form onSubmit={formik.handleSubmit}>
//             {toaster === false ? "" : <ToastApp />}
//             <Grid item xs={12} container spacing={2}>

//               <Grid xs={12} md={6} item>
//                 <TextField
//                   label={
//                     <span>
//                       {t("text.EnterDocName")} {requiredFields.includes('DocType') && (
//                         <span style={{ color: formik.values.doc_Name ? 'green' : 'red' }}>*</span>
//                       )}
//                     </span>
//                   }
//                   value={formik.values.doc_Name}
//                   placeholder={t("text.EnterDocName")}
//                   size="small"
//                   fullWidth
//                   name="doc_Name"
//                   id="doc_Name"
//                   style={{ backgroundColor: "white" }}
//                   onChange={formik.handleChange}
//                   onBlur={formik.handleBlur}
//                 />
//                 {formik.touched.doc_Name && formik.errors.doc_Name ? (
//                   <div style={{ color: "red", margin: "5px" }}>{formik.errors.doc_Name}</div>
//                 ) : null}
//               </Grid>

//               <Grid xs={12} md={6} item>
//                 <TextField
//                   label={t("text.EnterShortName")}
//                   value={formik.values.doc_shortname}
//                   placeholder={t("text.EnterShortName")}
//                   size="small"
//                   fullWidth
//                   name="doc_shortname"
//                   id="doc_shortname"
//                   style={{ backgroundColor: "white" }}
//                   onChange={formik.handleChange}
//                   onBlur={formik.handleBlur}
//                 />
//               </Grid>

//               <Grid item xs={12} spacing={2} style={{ display: 'flex', justifyContent: 'center' }}>
//                 <Grid xs={6} style={{ margin: '6px' }}>
//                   <Button
//                     type="submit"
//                     fullWidth
//                     style={{
//                       backgroundColor: `var(--header-background)`,
//                       color: "white",
//                       marginTop: "10px",
//                     }}

//                   >
//                     {isEdit ? t("text.update") : t("text.save")}
//                   </Button>
//                 </Grid>

//                 <Grid xs={6} style={{ margin: '6px' }}>
//                   <Button
//                     type="reset"
//                     fullWidth
//                     style={{
//                       backgroundColor: "#F43F5E",
//                       color: "white",
//                       marginTop: "10px",
//                     }}
//                     onClick={(e) => { formik.resetForm(); setIsEdit(false); }}
//                   >
//                     {t("text.reset")}
//                   </Button>
//                 </Grid>

//               </Grid>
//             </Grid>
//             {/* </Card> */}
//           </form>

//           {isLoading ? (
//             <div
//               style={{
//                 display: "flex",
//                 justifyContent: "center",
//                 alignItems: "center",
//               }}
//             >
//               <CircularProgress />
//             </div>
//           ) : (
//             <Box>
//               <br />
//               <div style={{ width: "100%", backgroundColor: `var(--grid-background)` }}>
//                 <DataGrid
//                   rows={Docs}
//                   columns={adjustedColumns}
//                   autoHeight
//                   slots={{
//                     toolbar: GridToolbar,
//                   }}
//                   rowSpacingType="border"
//                   pagination={true}
//                   pageSizeOptions={[5, 10, 25, 50, 100].map((size) => ({
//                     value: size,
//                     label: `${size}`,
//                   }))}
//                   initialState={{
//                     pagination: { paginationModel: { pageSize: 5 } },
//                   }}
//                   slotProps={{
//                     toolbar: {
//                       showQuickFilter: true,
//                     },
//                   }}
//                 />
//               </div>
//             </Box>
//           )}
//         </Paper>
//       </Card>
//       <ToastApp />
//     </>
//   );
// }
